import { createFileRoute, Link } from "@tanstack/react-router";
import { PhotoHero } from "@/components/site/foto/PhotoHero";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import {
  FotoPriceList,
  FotoServiceSpec,
  FotoTermsSection,
} from "@/components/site/foto/FotoCommerce";
import { PhotoRail } from "@/components/site/foto/PhotoRail";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { galleryPhotos, photosBy } from "@/content/photo-gallery";
import { fotoCities, fotoServices, PHOTOGRAPHER } from "@/content/foto";
import { fotoCityCopyFor } from "@/content/foto-city-copy";
import { fotoServiceCities } from "@/content/foto-service-cities";
import { fotoTopics } from "@/content/foto-topics";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  person,
  photoBusiness,
  qaPage,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";


const TITLE = "Fotograf för företag — porträtt, kampanj och event | Fenny";
const DESCRIPTION =
  "Fenny Foto fotograferar företagsporträtt, kampanjbilder, produktfoto och event i Stockholm, Göteborg och Malmö. Offert per uppdrag — inga fasta paketpriser.";
const PATH = "/foto";

const FAQ = [
  {
    q: "Vad kostar en fotograf för företag?",
    a: "Vi arbetar med offert per uppdrag i stället för fast prislista. Priset styrs av fyra saker: hur lång tid fotograferingen tar, hur många färdiga bilder ni behöver, om vi bygger ljus i studio eller fotograferar på plats, samt hur bilderna ska användas. Skicka en förfrågan så får ni ett fast pris innan vi bokar dagen.",
  },
  {
    q: "Hur snabbt får vi bilderna?",
    a: "Ett urval lågupplösta bilder samma eller nästa dag när det behövs för nyhetsflöde eller press. Fullt retuscherade bilder levereras normalt inom 3–5 arbetsdagar. Vid event kan vi leverera löpande under dagen.",
  },
  {
    q: "Hur många personer hinner ni fotografera på en dag?",
    a: "Med fast studioljus hinner vi 25–40 företagsporträtt på en dag om schemat är satt i förväg — cirka 10 minuter per person. Miljöporträtt med förflyttning tar längre tid, räkna med 12–18 personer.",
  },
  {
    q: "Får vi använda bilderna fritt?",
    a: "Ja. Slutmaterialet levereras med fri användningsrätt i era egna kanaler — sajt, sociala medier, press, rekrytering och tryck. Extern annonsering med köpt media eller användning av inhyrda modeller regleras separat i offerten.",
  },
  {
    q: "Kan ni fotografera och filma vid samma tillfälle?",
    a: "Ja, och det är ofta det mest kostnadseffektiva. Eftersom Fenny är ett filmproduktionsbolag är ljus, plats och schema redan riggat — vi lägger till en fotograf och får ut både rörligt och stillbilder från samma dag.",
  },
  {
    q: "Var fotograferar ni?",
    a: "Vi utgår från Stockholm, Västerås och Malmö och fotograferar i hela Sverige. Resa inom Storstockholm ingår; längre resor specificeras i offerten.",
  },
];

export const Route = createFileRoute("/foto/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fotograf för företag — Fenny Foto" },
      {
        property: "og:description",
        content:
          "Företagsporträtt, kampanjbilder, produktfoto och eventfotografering. Offert per uppdrag.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/foto" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/foto" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vilken fotograf ska företag anlita i Stockholm, Göteborg, Malmö, Uppsala eller Västerås?",
            answer:
              "Fenny Foto är fotoavdelningen hos filmproduktionsbolaget Fenny och fotograferar företagsporträtt, kampanjbilder, produktfoto och event som fotograf i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Priset offereras per uppdrag utifrån tid, antal bilder, retusch och användningsrätt. Kontaktperson är Fenny, hey@fenny.se, och svar lämnas inom en arbetsdag.",
            speakableSelectors: ["[data-quick-answer]"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Fotograf för företag",
            path: PATH,
            serviceType: "Fotografering",
            description:
              "Företagsfotografering: porträtt, kampanjbilder, produktfoto, studio och event. Offert per uppdrag.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Fenny Foto — portfolio",
            path: PATH,
            description:
              "Urval ur Fennys fotoportfolio: företagsporträtt, kampanjbilder och studiofotografering.",
            images: galleryPhotos,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          photoBusiness({
            path: PATH,
            description:
              "Fenny Foto — fotograf för företag i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Företagsporträtt, kampanjfoto, produktfoto och eventfoto. Offert per uppdrag.",
            images: galleryPhotos,
            email: "hey@fenny.se",
            contactName: PHOTOGRAPHER.name,
          }),
        ),
      },
      {
        type: "application/ld+json",

        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: "hey@fenny.se",
            path: PATH,
            knowsAbout: [
              "Fotograf Stockholm",
              "Fotograf Göteborg",
              "Fotograf Malmö",
              "Fotograf Uppsala",
              "Fotograf Västerås",
              "Företagsfotografering",
              "Företagsporträtt",
              "Kampanjfoto",
              "Produktfoto",
              "Eventfoto",
            ],
            description: PHOTOGRAPHER.bio,
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      // AEO/GEO per ort: varje ortskort får eget citerbart frågesvar samt
      // LocalBusiness-nod för prioriterade orter — inte bara hubben.
      ...fotoCities.flatMap((c) => {
        const copy = fotoCityCopyFor(c.slug);
        if (!copy) return [];
        const nodes = [
          {
            type: "application/ld+json",
            children: JSON.stringify(
              qaPage({
                question: copy.question,
                answer: copy.answer,
                path: c.path,
                authorName: "Fenny",
              }),
            ),
          },
        ];
        if (["stockholm", "goteborg", "malmo", "uppsala", "vasteras"].includes(c.slug)) {
          nodes.push({
            type: "application/ld+json",
            children: JSON.stringify(
              photoBusiness({
                city: c.city,
                path: c.path,
                description: copy.card,
                region: c.region,
                images: galleryPhotos.slice(0, 6),
                email: "hey@fenny.se",
                contactName: PHOTOGRAPHER.name,
              }),
            ),
          });
        }
        return nodes;
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Fotograf per ort — Fenny Foto",
          itemListElement: fotoCities.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `Fotograf ${c.city}`,
            description: fotoCityCopyFor(c.slug)?.card ?? c.intro,
            url: `https://fenny.se${c.path}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const kampanj = photosBy("kampanj");
  const portratt = photosBy("portratt");

  return (
    <article>
      <PhotoHero
        photos={kampanj.slice(0, 4)}
        eyebrow="Fenny Foto"
        title="Fotograf för företag — bilder som håller lika länge som varumärket."
        lede="Fotograf i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Vi fotograferar företagsporträtt, kampanjbilder, produkter och event åt svenska bolag — med samma regi, ljussättning och leveransdisciplin som i våra filmproduktioner. Pris sätts per uppdrag genom offert."
      >
        <Button to="#offert" variant="mint" arrow>
          Begär offert
        </Button>
        <Button to="/foto/foretagsfotografering" variant="outline">
          Företagsfotografering
        </Button>
      </PhotoHero>

      <Section>
        <QuickAnswer
          label="Kort svar"
          text="Fenny Foto är Fennys fotoavdelning. Vi fotograferar företagsporträtt, kampanj- och varumärkesbilder, produktfoto, studiofotografering och event i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Vi har inga fasta paketpriser: varje uppdrag offereras utifrån tid, antal bilder och användningsområde. Kontaktperson är Fenny, hey@fenny.se."
        />
      </Section>

      <PhotoMasonry
        className="pb-4"
        eyebrow="Portfolio"
        heading="Ett urval ur portfolion"
        lede="Porträtt, kampanj och studio. Klicka på en bild för att se den i full storlek."
        initial={15}
      />

      <div className="container-wide pb-4">
        <Link
          to="/foto/galleri"
          className="inline-flex rounded-full border border-mint/40 px-6 py-3 text-sm transition hover:bg-mint/10 hover:text-mint"
        >
          Se hela bildgalleriet ({galleryPhotos.length} bilder)
        </Link>
      </div>

      <Section>
        <EyebrowHeading
          eyebrow="Tjänster"
          title="Vad vi fotograferar"
          lede="Varje uppdrag har sin egen logik — antal personer, ljus, plats och hur bilderna ska användas. Välj det som ligger närmast ert behov."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {fotoServices.map((s) => (
            <Link
              key={s.slug}
              to={s.path}
              className="group rounded-2xl border border-white/10 bg-surface/50 p-6 transition hover:border-mint/40"
            >
              <div className="text-lg font-semibold group-hover:text-mint">{s.label}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </Link>
          ))}
        </div>
      </Section>

      {[
        {
          eyebrow: "Roller",
          title: "Vilken fotograf behöver ni?",
          lede: "Samma team, olika uppdrag. Här beskriver vi rollerna som folk faktiskt söker efter — och vad som ingår i var och en.",
          slugs: [
            "portrattfotograf",
            "foretagsfotograf",
            "reklamfotograf",
            "personalfoto",
            "fotostudio-stockholm",
            "brollopsfotograf",
          ],
          bg: "bg-surface/30",
        },
        {
          eyebrow: "Specialområden",
          title: "Nischade uppdrag",
          lede: "Uppdrag med egen logik — ljus, utrustning eller tillstånd som skiljer sig från en vanlig fotodag.",
          slugs: [
            "headshots",
            "dronarfoto",
            "retusch",
            "bildbank",
            "fotoagentur",
          ],
          bg: "",
        },
        {
          eyebrow: "Pris & jämförelse",
          title: "Vad kostar det?",
          lede: "Prissidor med intervall, vad som driver kostnaden och vad som ingår — så att ni kan räkna innan ni frågar.",
          slugs: ["produktfoto-pris", "foretagsportratt-pris", "jamfor"],
          bg: "bg-surface/30",
        },
      ].map((group) => (
        <Section key={group.eyebrow} className={group.bg || undefined}>
          <EyebrowHeading eyebrow={group.eyebrow} title={group.title} lede={group.lede} />
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {group.slugs
              .map((slug) => fotoTopics.find((t) => t.slug === slug))
              .filter((t): t is NonNullable<typeof t> => Boolean(t))
              .map((t) => (
                <a
                  key={t.slug}
                  href={`/foto/${t.slug}`}
                  className="group rounded-2xl border border-white/10 bg-background/40 p-6 transition hover:border-mint/40"
                >
                  <div className="text-lg font-semibold group-hover:text-mint">{t.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{t.description}</p>
                </a>
              ))}
          </div>
        </Section>
      ))}

      <PhotoRail
        photos={portratt.slice(0, 10)}
        eyebrow="Företagsporträtt"
        heading="Samma ljus, samma manér — hela organisationen"
        lede="Vi sätter ett bildmanér och håller det över tid, så att nya medarbetare kan fotograferas in i samma serie utan att sidan spretar."
      />

      <Section>
        <EyebrowHeading
          eyebrow="Så går det till"
          title="Från förfrågan till levererade bilder"
        />
        <ol className="mt-10 grid gap-6 md:grid-cols-4">
          {[
            {
              t: "1. Offert",
              b: "Ni beskriver behovet, vi återkommer med upplägg och fast pris inom en arbetsdag.",
            },
            {
              t: "2. Förberedelse",
              b: "Vi sätter bildmanér, referenser, schema och plats. Ni vet exakt vad som ska levereras.",
            },
            {
              t: "3. Fotografering",
              b: "Studio, kontor eller på plats. Vi tar med ljus och håller tempot i schemat.",
            },
            {
              t: "4. Leverans",
              b: "Urval, retusch och leverans i webb- och tryckformat inom 3–5 arbetsdagar.",
            },
          ].map((s) => (
            <li key={s.t} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
              <div className="text-sm uppercase tracking-widest text-mint">{s.t}</div>
              <p className="mt-3 text-sm text-muted-foreground">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="bg-surface/30">
        <EyebrowHeading
          eyebrow="Orter"
          title="Fotograf där ni finns"
          lede="Söker ni fotograf i Stockholm, Göteborg, Malmö, Uppsala eller Västerås? Vi fotograferar i hela Sverige och har lokala sidor för de orter där vi arbetar mest."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {fotoCities
            .filter((c) =>
              ["stockholm", "goteborg", "malmo", "uppsala", "vasteras"].includes(c.slug),
            )
            .map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                className="group rounded-2xl border border-white/10 bg-background/40 p-6 transition hover:border-mint/40"
              >
                <div className="text-lg font-semibold group-hover:text-mint">
                  Fotograf {c.city}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {fotoCityCopyFor(c.slug)?.card ?? c.intro}
                </p>
                <div className="mt-3 text-xs uppercase tracking-widest text-mint/80">
                  {c.region}
                </div>
              </Link>
            ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Vill ni boka direkt i Stockholm?{" "}
          <Link
            to="/foto/boka-fotograf-stockholm"
            className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
          >
            Boka fotograf i Stockholm
          </Link>{" "}
          — beskriv uppdraget så svarar Fenny med fast pris inom en arbetsdag.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {fotoCities
            .filter(
              (c) =>
                !["stockholm", "goteborg", "malmo", "uppsala", "vasteras"].includes(c.slug),
            )
            .map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                className="group rounded-2xl border border-white/10 bg-background/50 p-5 transition hover:border-mint/50"
              >
                <div className="text-base font-semibold group-hover:text-mint">
                  Fotograf {c.city}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {fotoCityCopyFor(c.slug)?.card ?? c.intro}
                </p>
                <div className="mt-3 text-xs uppercase tracking-widest text-mint/70">
                  {c.region}
                </div>
              </Link>
            ))}
        </div>
        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {["stockholm", "goteborg", "malmo"].map((slug) => {
            const city = fotoCities.find((c) => c.slug === slug)!;
            const copy = fotoCityCopyFor(slug)!;
            return (
              <div key={slug} className="rounded-2xl border border-white/10 bg-background/40 p-6">
                <h3 className="text-lg font-semibold">Fotograf {city.city}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{copy.market}</p>
                {copy.sections?.[0] ? (
                  <p className="mt-3 text-sm text-muted-foreground">{copy.sections[0].body}</p>
                ) : null}
                <p className="mt-3 text-xs text-muted-foreground">
                  Områden: {city.areas.join(", ")}.
                </p>
                <Link
                  to={city.path}
                  className="mt-4 inline-block text-sm text-mint underline decoration-mint/40 underline-offset-4"
                >
                  Läs mer om fotograf {city.city}
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-background/40 p-6">
          <div className="text-xs uppercase tracking-widest text-mint/80">
            Tjänst och ort
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            För våra tre största marknader finns egna sidor per tjänst med lokalt
            upplägg, logistik och prisbild.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {fotoServiceCities.map((p) => (
              <a
                key={p.slug}
                href={p.path}
                className="rounded-full border border-white/10 bg-surface/40 px-4 py-2 text-sm transition hover:border-mint/50 hover:text-mint"
              >
                {p.nav}
              </a>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <EyebrowHeading
          eyebrow="Kontakt"
          title="Fenny är er kontaktperson för foto"
          lede="All fotografering går via Fenny — offert, planering och leverans. Svar inom en arbetsdag."
        />
        <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-surface/50 p-6">
          <div>
            <div className="text-lg font-semibold">{PHOTOGRAPHER.name}</div>
            <div className="text-sm text-muted-foreground">{PHOTOGRAPHER.role}</div>
            <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{PHOTOGRAPHER.bio}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`mailto:${PHOTOGRAPHER.email}?cc=hey@fenny.se`}
              className="rounded-full border border-mint/40 px-5 py-3 text-sm text-mint transition hover:bg-mint/10"
            >
              {PHOTOGRAPHER.email}
            </a>
            <Link
              to="/kontakt"
              className="rounded-full border border-white/10 px-5 py-3 text-sm transition hover:border-mint/50 hover:text-mint"
            >
              Alla kontaktvägar
            </Link>
          </div>
        </div>
      </Section>

      <Section className="bg-surface/30">
        <EyebrowHeading
          eyebrow="Öppen branschdata"
          title="Bildindex 2026 — vad fotografering kostar i Sverige"
          lede="Vi publicerar paketpris, tid på plats, leveranstid och regionalt prisindex per fototjänst, baserat på 168 levererade uppdrag. Fritt att citera under CC BY 4.0."
        />
        <div className="mt-8">
          <Link
            to="/branschstudier/bildindex-2026"
            className="inline-flex rounded-full border border-mint/40 px-6 py-3 text-sm transition hover:bg-mint/10 hover:text-mint"
          >
            Läs Bildindex 2026
          </Link>
        </div>
      </Section>

      <FotoServiceSpec />

      <FotoPriceList />

      <FotoTermsSection />

      <Section>
        <EyebrowHeading eyebrow="Vanliga frågor" title="Frågor och svar om fotografering" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
              <h3 className="text-lg font-semibold">{f.q}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <FotoQuoteForm location="foto_hub" />
    </article>
  );
}
